import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

/**
 * AuthCallback Component
 * Handles the redirect from Microsoft OAuth
 * URL: /auth/callback?success=true
 */
export const AuthCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState('processing');

  useEffect(() => {
    const handleCallback = async () => {
      const success = searchParams.get('success');

      if (success === 'true') {
        setStatus('success');

        try {
          // Refresh user data from session
          await refreshUser();
          // Redirect to main app
          navigate('/projects', { replace: true });
        } catch (error) {
          console.error('Failed to load user after OAuth:', error);
          setStatus('error');
          // Redirect to login with error
          navigate('/login?error=auth_failed', { replace: true });
        }
      } else {
        setStatus('error');
        // OAuth failed, redirect to login with error
        navigate('/login?error=auth_failed', { replace: true });
      }
    };

    handleCallback();
  }, [searchParams, refreshUser, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        {status === 'processing' && (
          <>
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-gray-600">Completing sign in...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="inline-block">
              <svg
                className="w-12 h-12 text-green-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <p className="mt-4 text-gray-600">Sign in successful! Redirecting...</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="inline-block">
              <svg
                className="w-12 h-12 text-red-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <p className="mt-4 text-gray-600">Sign in failed. Redirecting to login...</p>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
